/**
 * Session-death detection (2026-07-27 security audit fix; wiring corrected
 * same day after a white-box review found the original fix inert).
 *
 * ROOT CAUSE: revoking a session (admin "deactivate member", platform
 * "deactivate tenant user", etc.) deletes the `auth.sessions` row. That only
 * breaks GoTrue-VALIDATED calls (edge functions, `getUser()`, token
 * refresh) -- they hit the DB and find no matching session. PostgREST /
 * Realtime / Storage validate the JWT SIGNATURE only, so a revoked device
 * keeps full DB read/write access until the access token itself expires.
 * Nothing in the client used to treat an edge-function 401 as "the session
 * might be dead" -- this module is that missing signal.
 *
 * WIRING (P0-1 fix): the first version of this patched
 * `supabase.functions.invoke` in src/supabase.js. That was dead code --
 * `SupabaseClient.functions` is a GETTER that constructs a brand-new
 * FunctionsClient on every property access, so the patched method was never
 * the one any of the 81 call sites actually invoked. The real interception
 * point is the shared `fetch` handed to `createClient({ global: { fetch } })`
 * -- every Supabase sub-client (auth/PostgREST/Storage/Functions/Realtime)
 * routes through that one function, so it cannot be bypassed by a
 * freshly-constructed client. See `authTimeoutFetch` in src/supabase.js: on
 * any non-OK `/functions/v1/` response it calls checkResponseForSessionDeath
 * below with a CLONED Response, fire-and-forget, never altering what the
 * real caller receives.
 *
 * THE FALSE-POSITIVE GUARD (load-bearing): a 401 UNAUTHORIZED from an edge
 * function can ALSO mean "your token briefly raced a refresh" on an
 * otherwise-perfectly-alive session. And a 403 FORBIDDEN (including
 * MFA_REQUIRED, see supabase/functions/_shared/requireAal2.ts) means
 * "you're not allowed to do that" on a session that is very much alive --
 * signing that user out would be a self-inflicted denial of service. So:
 *   1. shouldProbeSessionDeath() only flags the narrow 401+UNAUTHORIZED
 *      shape (see e.g. supabase/functions/platform-delete-tenant/index.ts
 *      :123 for the 401 UNAUTHORIZED case vs :141/:144 for 403 FORBIDDEN,
 *      which must NEVER reach this path).
 *   2. Even then, we don't take that 401 at face value -- we PROBE with a
 *      GoTrue-validated `getUser()` call. Only a confirmed
 *      AuthSessionMissingError (the session row is actually gone) results
 *      in a sign-out. A merely-unauthorized-but-alive session sails
 *      through untouched.
 *   3. The probe bails out entirely when there is no LOCAL session at all
 *      (see probeSessionLiveness) -- a pre-login edge-function 401 (e.g.
 *      log-login-failed, fired before any session exists) must never set a
 *      flag or trigger a sign-out; there's nothing to sign out of.
 */

import { supabase } from '../../supabase';

const REVOKED_FLAG_KEY = 'cv_access_revoked';
/** Self-serve / other-device revoke (distinct copy from admin accessRevoked). */
const SESSION_REVOKED_ELSEWHERE_KEY = 'cv_session_revoked_elsewhere';

/**
 * The flag is set for the duration of a probe round-trip (bounded by the
 * auth fetch timeout, up to ~20s -- see AUTH_FETCH_TIMEOUT_MS in
 * src/supabase.js). Without a max age, a flag left over from an in-flight
 * (or just-finished) probe could still be sitting in sessionStorage when an
 * UNRELATED sign-out happens moments later (e.g. an idle-timeout firing in
 * the same window) and get mislabeled as "access revoked" instead of the
 * correct "session expired". Consumers must check freshness, not just
 * presence -- see isRevokedFlagFresh().
 */
const REVOKED_FLAG_MAX_AGE_MS = 30 * 1000;

/**
 * Marks the revoked flag, timestamped so isRevokedFlagFresh() can expire it.
 * Also used directly by AuthContext's periodic-deactivation-check path (an
 * admin-initiated deactivation detected outside this module) so both origins
 * of the flag share the same timestamped format.
 */
export function setRevokedFlag() {
  try {
    window.sessionStorage.setItem(REVOKED_FLAG_KEY, String(Date.now()));
  } catch (_) {
    /* private mode / storage unavailable -- best effort only */
  }
}

export function clearRevokedFlag() {
  try {
    window.sessionStorage.removeItem(REVOKED_FLAG_KEY);
  } catch (_) {
    /* private mode / storage unavailable -- best effort only */
  }
}

/** Set when this device was signed out via Account Sessions / revoke-others. */
export function setSessionRevokedElsewhereFlag() {
  try {
    window.sessionStorage.setItem(SESSION_REVOKED_ELSEWHERE_KEY, String(Date.now()));
  } catch (_) {
    /* private mode / storage unavailable -- best effort only */
  }
}

export function clearSessionRevokedElsewhereFlag() {
  try {
    window.sessionStorage.removeItem(SESSION_REVOKED_ELSEWHERE_KEY);
  } catch (_) {
    /* private mode / storage unavailable -- best effort only */
  }
}

export function isSessionRevokedElsewhereFlagFresh() {
  try {
    const raw = window.sessionStorage.getItem(SESSION_REVOKED_ELSEWHERE_KEY);
    if (!raw) return false;
    const setAt = Number(raw);
    if (!Number.isFinite(setAt)) return true;
    return Date.now() - setAt < REVOKED_FLAG_MAX_AGE_MS;
  } catch (_) {
    return false;
  }
}

/**
 * True when the revoked flag is present AND was set within the last
 * REVOKED_FLAG_MAX_AGE_MS. AuthContext's SIGNED_OUT handler uses this
 * (instead of a plain presence check) to decide "access revoked" vs the
 * generic "session expired" copy -- see AuthContext.js.
 */
export function isRevokedFlagFresh() {
  try {
    const raw = window.sessionStorage.getItem(REVOKED_FLAG_KEY);
    if (!raw) return false;
    const setAt = Number(raw);
    // Non-numeric/corrupt value -- fail open (treat as fresh) rather than
    // silently swallow a real revocation over a parsing edge case. A
    // numeric-but-old value (e.g. a legacy bare '1' from before this flag
    // was timestamped) parses as an ancient epoch ms and is correctly
    // judged stale by the age check below.
    if (!Number.isFinite(setAt)) return true;
    return Date.now() - setAt < REVOKED_FLAG_MAX_AGE_MS;
  } catch (_) {
    return false;
  }
}

/**
 * PURE, DOM-free decision helper. Given a normalized `{ status, code }`
 * failure shape, returns true ONLY when it looks like the specific
 * "edge function rejected an unauthenticated/dead-session token" case.
 *
 * Deliberately returns false for:
 *   - 403 FORBIDDEN / MFA_REQUIRED -- "not allowed", not "not logged in".
 *   - any non-401 status.
 *   - a 401 whose code isn't exactly UNAUTHORIZED.
 *   - network failures / malformed input (no status at all).
 */
export function shouldProbeSessionDeath(error) {
  if (!error || typeof error !== 'object') return false;
  // PostgREST db_pre_request enforce_live_auth_session → PT401 + message session_revoked
  if (error.message === 'session_revoked') return true;
  if (error.status === 401 && error.code === 'PT401') return true;
  // Edge function dead-session shape
  return error.status === 401 && error.code === 'UNAUTHORIZED';
}

/**
 * Unconditionally probes whether the CURRENT local session is actually
 * still alive, using a GoTrue-validated call (unlike PostgREST/Realtime/
 * Storage, `getUser()` hits the auth server and checks `auth.sessions`).
 *
 * Bails out immediately (no flag, no probe) when there is no local session
 * at all: auth-js's `getUser()` RETURNS (does not throw) an
 * AuthSessionMissingError in that case, without ever removing a session or
 * firing SIGNED_OUT -- there's nothing to remove. Without this bail, a
 * pre-login edge-function 401 (log-login-failed etc., fired before any
 * session exists) would set the revoked flag with no corresponding
 * sign-out event to ever consume it -- SIGNED_IN only clears
 * cv_session_expired, not this flag -- so it would survive into the next
 * login and mislabel it.
 *
 * The revoked flag is set BEFORE probing, not after: when the session IS
 * genuinely alive-but-revoked, `supabase.auth.getUser()` internally removes
 * the local session and synchronously (awaited, within the same call) fires
 * + fully processes the SIGNED_OUT notification to every subscriber --
 * including AuthContext's onAuthStateChange handler -- as part of resolving
 * this promise, not after. That handler reads this flag DURING that
 * notification to choose "access revoked" copy over the generic "session
 * expired" copy, so the flag must already be set by the time getUser()
 * resolves.
 */
/**
 * In-flight probe, shared by every concurrent caller. The platform dashboard
 * invokes several edge functions on mount, so a revoked session produces a
 * BURST of 401s. Without this, each one drives its own refreshSession() —
 * auth-js serialises them, so they become N sequential REAL token rotations.
 * Under `enable_refresh_token_rotation` with a 10s reuse window
 * (supabase/config.toml), every extra rotation widens the window where a
 * lost response looks like token replay and gets the whole family revoked.
 * One probe per burst is all the information there is to gain.
 */
let inFlightProbe = null;
/** Last probe completion (ms). Cheap cooldown so a 401 loop cannot spin. */
let lastProbeAt = 0;
const PROBE_COOLDOWN_MS = 10 * 1000;

async function probeSessionLiveness() {
  if (inFlightProbe) return inFlightProbe;
  if (Date.now() - lastProbeAt < PROBE_COOLDOWN_MS) return undefined;
  inFlightProbe = runProbe().finally(() => {
    inFlightProbe = null;
    lastProbeAt = Date.now();
  });
  return inFlightProbe;
}

/** Test seam: clears the dedupe/cooldown between cases. */
export function __resetSessionLivenessProbeStateForTests() {
  inFlightProbe = null;
  lastProbeAt = 0;
}

async function runProbe() {
  const { data: { session } = {} } = await supabase.auth.getSession();
  if (!session) return;

  setRevokedFlag();
  let confirmedDead = false;
  try {
    // DO NOT probe with getUser(). It sends the CURRENT access token to
    // GoTrue, so a merely-EXPIRED token (routine — they now live 900s) is
    // rejected and reported as AuthSessionMissingError. Treating that as
    // death signed out healthy users every ~15 minutes: the exact
    // false-positive this detection was supposed to avoid, and worse than
    // the bug it fixes (2026-07-28 regression, reported in-session).
    //
    // The honest discriminator is whether the session can still REFRESH:
    //   • revoked  -> the auth.sessions row is deleted, so the refresh
    //                 token is gone and refreshSession() fails.
    //   • expired  -> the refresh token is intact, refreshSession()
    //                 succeeds and hands back a live session.
    // Only a FAILED refresh means the session is genuinely dead.
    // ...but "refresh returned an error" is NOT the same as "dead". Verified
    // in auth-js source (GoTrueClient `_callRefreshToken` catch):
    //
    //     if (isAuthError(error)) {
    //       const result = { data: null, error };
    //       if (!isAuthRetryableFetchError(error)) {
    //         await this._removeSession();      // <- ONLY for non-retryable
    //       }
    //       return result;                      // <- RETURNS, does not throw
    //     }
    //
    // So a RETRYABLE failure (offline, DNS/CORS, 502/503/504, Cloudflare
    // 52x, or our own 20s AbortSignal timeout on /auth/v1/ — i.e. every
    // ordinary iOS-PWA hiccup) comes back as {session: null, error} with the
    // session DELIBERATELY KEPT. auth-js is saying "I don't know, hold on to
    // it." Reading that as death is what signed healthy users out. Only a
    // NON-retryable failure (refresh_token_not_found => the auth.sessions row
    // is gone) is real revocation.
    const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
    confirmedDead = !!refreshError
      && !refreshed?.session
      && refreshError.name !== 'AuthRetryableFetchError';
    // NOTE: no signOut() here, deliberately. On a non-retryable failure
    // auth-js has ALREADY called _removeSession() and fully dispatched
    // SIGNED_OUT before refreshSession() resolves — AuthContext has already
    // consumed the (still-fresh) revoked flag and shown the right copy. A
    // second signOut() fires a SECOND SIGNED_OUT whose flag is now cleared,
    // so AuthContext takes the else-branch and CLOBBERS "access revoked"
    // with the generic "session expired". Doing nothing is correct.
  } finally {
    // Only clear when NOT confirmed dead -- covers both a false positive
    // (the session refreshed fine, the triggering call failed on its own
    // terms) and refreshSession() itself rejecting (network drop, or
    // NavigatorLockAcquireTimeoutError, which auth-js can throw and is not
    // an AuthError). A `finally` runs even on rejection, so the flag can
    // never leak past this call, and a throw leaves confirmedDead false --
    // i.e. we fail SAFE: never sign anyone out on an inconclusive probe.
    if (!confirmedDead) clearRevokedFlag();
  }
  // else: confirmed dead. getUser() has already removed the local session
  // and fired SIGNED_OUT; nothing further to do here.
}

/**
 * The action: probes for session death ONLY when the triggering failure
 * passes shouldProbeSessionDeath(). Swallows every error internally --
 * this must never break the caller's (edge-function-invoking) flow.
 */
export async function handlePossibleSessionDeath(error) {
  try {
    if (!shouldProbeSessionDeath(error)) return;
    // Prefer "signed out elsewhere" copy when the REST gate said so.
    if (error?.message === 'session_revoked' || error?.code === 'PT401') {
      setSessionRevokedElsewhereFlag();
    }
    await probeSessionLiveness();
  } catch (_) {
    // Fail-safe: a bug in liveness detection must never surface as a
    // broken app -- worst case we simply miss this one death signal.
  }
}

/**
 * Throttled resume-time liveness check (see the role-agnostic resume effect
 * in AuthContext.js). Unconditional -- callers gate the throttle themselves,
 * this just runs the probe. Never throws.
 */
export async function checkSessionLivenessOnResume() {
  // INTENTIONALLY A NO-OP (2026-07-28).
  //
  // This used to probe on every resume. That was survivable when the probe
  // was a read-only getUser(), but the probe is now refreshSession() -- a
  // MUTATING call that rotates the refresh token. Supabase has refresh-token
  // rotation ON with a 10s reuse window, so a speculative rotation racing
  // auth-js's own auto-refresh can be flagged as a stolen-token replay and
  // REVOKE the session: precisely the spurious sign-out this module exists
  // to prevent. Probing on a hunch is not worth that risk.
  //
  // Nothing is lost. A revoked session is still caught, by a real signal
  // rather than a guess:
  //   • any edge-function call 401s -> the fetch interceptor probes (and the
  //     platform dashboard invokes one on mount, which is the reported case);
  //   • auto-refresh fails on its own and auth-js fires SIGNED_OUT;
  //   • DB access dies at access-token expiry (900s) regardless.
  //
  // Kept as an exported no-op so the resume seam stays documented and a
  // future passive (non-mutating) liveness check has an obvious home.
}

/**
 * Normalizes a fetch `Response` from an edge-function call into the
 * `{ status, code }` shape shouldProbeSessionDeath()/handlePossibleSessionDeath()
 * expect, then runs the check. Called from `authTimeoutFetch` in
 * src/supabase.js with an ALREADY-CLONED Response -- reading the body here
 * must never consume the body the real caller (supabase-js FunctionsClient)
 * is about to read itself.
 */
export async function checkResponseForSessionDeath(response) {
  try {
    if (!response || response.ok) return;
    const status = typeof response.status === 'number' ? response.status : null;
    let code = null;
    let message = null;
    try {
      const body = await response.json();
      // Edge functions: { error: { code } }. PostgREST: { code, message }.
      if (typeof body?.error?.code === 'string') code = body.error.code;
      else if (typeof body?.code === 'string') code = body.code;
      if (typeof body?.message === 'string') message = body.message;
      else if (typeof body?.error?.message === 'string') message = body.error.message;
    } catch (_) {
      // Body wasn't JSON / empty -- fall through with what we have.
    }
    await handlePossibleSessionDeath({ status, code, message });
  } catch (_) {
    // Fail-safe: never break the invoking fetch path.
  }
}
